'use client';

import { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { type ApolloError, useQuery } from '@apollo/client';
import { GetProjectFieldDefinitions } from '@frameio/graphql-client/generated';
import { GetVideoAssetsReframeMetadata } from '@frameio/graphql-client/graphql/features/assets/queries/generated/GetVideoAssetsReframeMetadata';
import {
  findVideoCodecFieldId,
  MAX_ASSETS_LIMIT_REFRAME_VIDEO,
  type ValidationErrorKind,
} from '@frameio/graphql-client/graphql/features/assets/queries/reframeValidation';
import {
  useActiveAccountId,
  useActiveProjectId,
} from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing/getLink';
import { useTrack } from '@frameio/shared/segment/hooks';
import {
  Button2,
  Dialog,
  InlineButton,
  Spinner,
  Text,
  TextField,
  theme,
  Toggle,
  ToggleCheckbox,
  ToggleLabel,
  ToggleSwitch,
  Tooltip,
} from '@frameio/vapor';
import {
  IconChevronRight,
  IconInfo,
  IconWarningFill,
} from '@frameio/vapor-icons';
import { DialogFooter } from '../components/DialogFooter';
import { DialogHeader } from '../components/DialogHeader';
import { Divider } from '../components/Divider';
import {
  ASPECT_RATIO_OPTIONS,
  type AspectRatioOption,
} from '../constants/aspectRatio';
import { SHARED_TEXT } from '../constants/sharedText';
import { useCreateReframeVideoOperationMutation } from '../gql/mutations/generated/CreateReframeVideoOperation';
import { useFireflyErrorToast } from '../hooks/useFireflyErrorToast';
import { useFireflyRateLimit } from '../hooks/useFireflyRateLimit';
import { useVariationsProcessingToast } from '../hooks/useVariationsProcessingToast';
import styles from './FireflyReframeDialog.module.css';
import TEXT from './generated/TEXT/FireflyReframeDialog';
import {
  type FocusSubjectErrorKind,
  MAX_FOCUS_SUBJECT_KEYWORD_LENGTH,
  MAX_FOCUS_SUBJECT_KEYWORDS,
  validateFocusSubject,
  validateReframeAssets,
} from './utils';

TEXT.DEFINE({
  /** Name of the reframe video action. */
  actionName: 'Reframe Video',
  /** Subtitle of the reframe video dialog. */
  subtitle: 'Resize your video to fit different aspect ratios.',
  /** This label is used to describe which subject in the video should be focused on. */
  focusSubject: 'Focus subject (Optional)',
  /** Focus subject placeholder. */
  focusSubjectPlaceholder: 'e.g. person, product or logo',
  /** Accessible label and tooltip for the focus subject info icon linking to docs. */
  focusSubjectInfoLabel: 'Learn more about focus subjects',
  /** Error shown when the focus subject contains disallowed characters. */
  focusSubjectInvalid:
    'Only letters, numbers, spaces, hyphens, underscores, and commas are allowed.',
  /** Error shown when the focus subject has more comma-separated keywords than allowed. */
  focusSubjectTooManyKeywords: ({ max }: { max: number }): string =>
    `You can enter up to ${max} keywords, separated by commas.`,
  /** Error shown when a single comma-separated keyword is too long. */
  focusSubjectKeywordTooLong: ({ max }: { max: number }): string =>
    `Each keyword must be ${max} characters or fewer.`,
  /** Aspect ratio label. */
  aspectRatio: 'Aspect Ratio',
  /** Aspect ratio placeholder. */

  aspectRatioPlaceholder: 'Select aspect ratios',
  /** This text is used to describe the limitations of the reframe video feature. */
  infoText:
    'This feature only supports H.264 and H.265 video files up to 30 minutes long.',
  /** Label for the link to documentation about the reframe video feature. */
  learnMore: 'Learn more',
  /** Generate button text. */
  generate: 'Generate Variations',
  /** Cancel button text. */
  cancel: 'Cancel',
  /** This text is used to close the dialog. */
  dialogCloseLabel: 'Close Dialog',
  /** Label for videos that exceed the maximum duration */
  overDurationLabel: 'Over 30 minutes',
  /** Label for videos with unsupported codec */
  invalidCodecLabel: 'Unsupported codec',
  /** Summary text for the validation warning accordion header */
  validationWarningSummary: ({
    failedCount,
    totalCount,
  }: {
    failedCount: number;
    totalCount: number;
  }): string => `${failedCount} of ${totalCount} videos won't be reframed`,
  /** Introduction text for validation warning in the expanded accordion */
  validationWarningIntro:
    'The following videos will not be included in reframe:',
  /** Continuation text for validation warning when some videos are valid */
  validationWarningContinue:
    'You can still generate variations for the remaining videos.',
  /** Label for OTIO file toggle */
  includeOtioFile: 'Include OTIO file',
  /** Tooltip explaining OTIO file option */
  includeOtioFileTooltip:
    'Creates an editable OTIO file for adjusting reframing in supported editing apps.',
  /** Summary text shown in the processing accordion header. */
  processingSummary: ({
    processingCount,
    totalCount,
  }: {
    processingCount: number;
    totalCount: number;
  }): string =>
    `${processingCount} of ${totalCount} videos are still processing`,
  /** Introduction text shown when the processing accordion is expanded. */
  processingIntro:
    'Videos below will be available for reframe once processing completes.',
  /** Per-row status shown next to a processing asset. */
  processingRowStatus: 'Processing…',
  /** Aria label for the small spinner indicating an asset is still processing. */
  processingSpinnerLabel: 'Processing',
});

const INFO_TEXT_HREF =
  'https://developer.adobe.com/audio-video-firefly-services/getting-started/usage/#reframe-api-usage';

const FOCUS_SUBJECT_INFO_HREF =
  'https://developer.adobe.com/audio-video-firefly-services/getting-started/semantic-search/';

function getErrorLabel(
  errorKind: ValidationErrorKind,
  codec?: string | null
): string {
  switch (errorKind) {
    case 'over_duration':
      return TEXT.overDurationLabel;
    case 'invalid_codec':
      if (codec != null && codec !== '') {
        return `${TEXT.invalidCodecLabel}: ${codec}`;
      }
      return TEXT.invalidCodecLabel;
    default: {
      const _exhaustive: never = errorKind;
      return _exhaustive;
    }
  }
}

function getFocusSubjectErrorMessage(kind: FocusSubjectErrorKind): string {
  switch (kind) {
    case 'invalid_characters':
      return TEXT.focusSubjectInvalid;
    case 'too_many_keywords':
      return TEXT.focusSubjectTooManyKeywords({
        max: MAX_FOCUS_SUBJECT_KEYWORDS,
      });
    case 'keyword_too_long':
      return TEXT.focusSubjectKeywordTooLong({
        max: MAX_FOCUS_SUBJECT_KEYWORD_LENGTH,
      });
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

type AccordionPanelProps = {
  children: React.ReactNode;
  isExpanded: boolean;
};

function AccordionPanel({ children, isExpanded }: AccordionPanelProps) {
  return (
    <AnimatePresence initial={false}>
      {isExpanded && (
        <motion.div
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          initial={{ height: 0, opacity: 0 }}
          style={{ overflow: 'hidden' }}
          transition={{
            duration: 0.25,
            ease: theme.transitions.curves.primary,
          }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

type FireflyReframeDialogProps = {
  assetIds?: string[];
  onClose?: () => void;
};

export const FireflyReframeDialog = ({
  assetIds = [],
  onClose,
}: FireflyReframeDialogProps) => {
  const [focusSubject, setFocusSubject] = useState('');
  const [selectedAspectRatios, setSelectedAspectRatios] = useState<string[]>(
    []
  );
  const [includeOtio, setIncludeOtio] = useState(false);
  const [isErrorsExpanded, setIsErrorsExpanded] = useState(false);
  const [isProcessingExpanded, setIsProcessingExpanded] = useState(false);

  const focusSubjectErrorKind = validateFocusSubject(focusSubject);
  const focusSubjectError =
    focusSubjectErrorKind !== null
      ? getFocusSubjectErrorMessage(focusSubjectErrorKind)
      : undefined;

  const [createReframeVideoOperation, { loading, called }] =
    useCreateReframeVideoOperationMutation();
  const track = useTrack();
  const showErrorToast = useFireflyErrorToast();
  const { handleRateLimitError, rateLimitDialog } = useFireflyRateLimit();
  const projectId = useActiveProjectId();
  const accountId = useActiveAccountId();
  const showVariationsProcessingToast = useVariationsProcessingToast();

  const { data: fieldDefinitionsData } = useQuery(GetProjectFieldDefinitions, {
    variables: {
      projectId: projectId!,
    },
    fetchPolicy: 'cache-first',
  });

  const videoCodecFieldId = findVideoCodecFieldId(
    fieldDefinitionsData?.account?.project?.fieldDefinitions?.nodes ?? []
  );

  const { data: assetsData } = useQuery(GetVideoAssetsReframeMetadata, {
    variables: {
      accountId: accountId ?? '',
      assetIds,
      first: assetIds.length,
      fieldDefinitionIds: [videoCodecFieldId!],
    },
    skip: !accountId || assetIds.length === 0 || !videoCodecFieldId,
    // cache-and-network so the project-wide AssetEventsByProject subscription
    // (ProjectAssetEventsSubscription) can flow transcode-complete updates
    // into this query via the Apollo cache and re-run validation.
    fetchPolicy: 'cache-and-network',
  });

  const { failedAssets, processingAssets, validAssetIds } = useMemo(
    () => validateReframeAssets(assetsData?.account?.assets?.nodes ?? []),
    [assetsData]
  );

  const tooManySelectedMessage = SHARED_TEXT.tooManySelectedText({
    actionName: TEXT.actionName,
    assetCount: MAX_ASSETS_LIMIT_REFRAME_VIDEO,
  });
  const startErrorMessage = SHARED_TEXT.errorMessageToast({
    actionName: TEXT.actionName,
  });
  const handleGenerate = useCallback(async () => {
    try {
      if (focusSubjectError != null) {
        return;
      }
      if (validAssetIds.length > MAX_ASSETS_LIMIT_REFRAME_VIDEO) {
        showErrorToast(tooManySelectedMessage);
        return;
      }

      const aspectRatiosInput = selectedAspectRatios
        .map((ratio) => {
          const [widthStr, heightStr] = ratio.split(':');
          const width = Number(widthStr);
          const height = Number(heightStr);
          if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
          return { width, height };
        })
        .filter((v): v is { width: number; height: number } => v !== null);

      const { errors, data } = await createReframeVideoOperation({
        variables: {
          input: {
            sourceAssetIds: validAssetIds,
            aspectRatios: aspectRatiosInput,
            prompt: focusSubject || undefined,
            sceneEditDetection: true,
            includeOtio,
          },
        },
      });
      if (errors?.length) {
        if (!handleRateLimitError(errors)) {
          showErrorToast(startErrorMessage);
        }
        return;
      }
      track('generation-started', {
        action_name: 'Video Reframe',
        number_of_assets: validAssetIds.length,
        parameters_selected: selectedAspectRatios.length,
      });
      const destinationId =
        data?.createReframeVideoOperation?.fireflyOperation?.destination?.id;
      const href = getLink({
        page: 'folder',
        projectId: projectId!,
        folderAssetId: destinationId,
      });
      showVariationsProcessingToast({ href, projectId: projectId! });
      onClose?.();
    } catch (error) {
      if (!handleRateLimitError(error as ApolloError)) {
        showErrorToast(startErrorMessage);
      }
    }
  }, [
    focusSubject,
    focusSubjectError,
    selectedAspectRatios,
    validAssetIds,
    createReframeVideoOperation,
    projectId,
    onClose,
    track,
    showVariationsProcessingToast,
    showErrorToast,
    handleRateLimitError,
    tooManySelectedMessage,
    startErrorMessage,
    includeOtio,
  ]);

  const handleClose = useCallback(() => {
    if (!called) {
      track('generation-dismissed', {
        action_name: 'Video Reframe',
        number_of_assets: assetIds.length,
        parameters_selected: selectedAspectRatios.length,
      });
    }
    onClose?.();
  }, [called, track, assetIds.length, selectedAspectRatios.length, onClose]);

  return (
    <Dialog
      enableClickOutsideToClose={false}
      isShowing={true}
      onRequestClose={handleClose}
      size="medium"
    >
      <DialogHeader
        closeLabel={TEXT.dialogCloseLabel}
        onClose={handleClose}
        subtitle={TEXT.subtitle}
        title={TEXT.actionName}
      />

      <Divider />

      <div className={styles.body}>
        <div className={styles.fieldGroup}>
          <div className={styles.labelRow}>
            <Text color="body" fontWeight="regular" variant="body">
              {TEXT.focusSubject}
            </Text>
            <Tooltip placement="right" title={TEXT.focusSubjectInfoLabel}>
              <InlineButton
                aria-label={TEXT.focusSubjectInfoLabel}
                onPress={() =>
                  window.open(
                    FOCUS_SUBJECT_INFO_HREF,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              >
                <IconInfo color={theme.colors.icon.primary} size={16} />
              </InlineButton>
            </Tooltip>
          </div>
          <TextField
            error={focusSubjectError}
            errorVariant="inline"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setFocusSubject(e.target.value)
            }
            placeholder={TEXT.focusSubjectPlaceholder}
            value={focusSubject}
          />
        </div>

        <div className={styles.fieldGroup}>
          <Text color="body" fontWeight="regular" variant="body">
            {TEXT.aspectRatio}
          </Text>
          <div className={styles.ratioGrid}>
            {ASPECT_RATIO_OPTIONS.map((opt: AspectRatioOption) => {
              const key = opt.id;
              const isSelected = selectedAspectRatios.includes(key);
              const width = opt.width;
              const height = opt.height;
              const isPortrait = height > width;
              const cssRatio = `${width}/${height}`;
              const isSquare = width === height;
              return (
                <button
                  key={key}
                  className={styles.ratioCard}
                  data-selected={String(isSelected)}
                  data-testid={`ratio-card-${key.replace(':', '_')}`}
                  onClick={() =>
                    setSelectedAspectRatios((prev) =>
                      prev.includes(key)
                        ? prev.filter((v) => v !== key)
                        : [...prev, key]
                    )
                  }
                >
                  <div className={styles.toggleCheckbox}>
                    <ToggleCheckbox checked={isSelected} />
                  </div>
                  <div
                    className={styles.ratioPreview}
                    data-portrait={String(isPortrait)}
                    data-square={String(isSquare)}
                    style={{ aspectRatio: cssRatio }}
                  >
                    <Text variant="body">{`${width}:${height}`}</Text>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {processingAssets.length > 0 && (
          <div className={styles.validationAccordion}>
            <button
              aria-expanded={isProcessingExpanded}
              className={styles.validationAccordionHeader}
              onClick={() => setIsProcessingExpanded((v) => !v)}
            >
              <div className={styles.accordionLabel}>
                <Spinner
                  aria-label={TEXT.processingSpinnerLabel}
                  isLoading={true}
                  size="xx-small"
                />
                <Text variant="body">
                  {TEXT.processingSummary({
                    processingCount: processingAssets.length,
                    totalCount: assetIds.length,
                  })}
                </Text>
              </div>
              <IconChevronRight
                className={styles.chevron}
                data-expanded={String(isProcessingExpanded)}
                size={16}
              />
            </button>

            <AccordionPanel isExpanded={isProcessingExpanded}>
              <div className={styles.validationAccordionContent}>
                <Text color="secondary" variant="body">
                  {TEXT.processingIntro}
                </Text>
                <div className={styles.validationListContainer}>
                  <ul className={styles.validationList}>
                    {processingAssets.map((asset) => (
                      <li key={asset.id} className={styles.validationListItem}>
                        <div className={styles.processingItemRow}>
                          <Spinner
                            aria-labelledby={`processing-status-${asset.id}`}
                            isLoading={true}
                            size="x-small"
                          />
                          <div className={styles.validationItemContent}>
                            <Text
                              as="span"
                              display="block"
                              fontWeight="semiBold"
                              variant="body"
                            >
                              {asset.name || asset.id}
                            </Text>
                            <Text
                              as="span"
                              color="secondary"
                              display="block"
                              id={`processing-status-${asset.id}`}
                              variant="body"
                            >
                              {TEXT.processingRowStatus}
                            </Text>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </AccordionPanel>
          </div>
        )}

        {failedAssets.length > 0 && (
          <div className={styles.validationAccordion}>
            <button
              aria-expanded={isErrorsExpanded}
              className={styles.validationAccordionHeader}
              onClick={() => setIsErrorsExpanded((v) => !v)}
            >
              <div className={styles.accordionLabel}>
                <IconWarningFill
                  color={theme.colors.meta.icon.yellow}
                  size={16}
                />
                <Text variant="body">
                  {TEXT.validationWarningSummary({
                    failedCount: failedAssets.length,
                    totalCount: assetIds.length,
                  })}
                </Text>
              </div>
              <IconChevronRight
                className={styles.chevron}
                data-expanded={String(isErrorsExpanded)}
                size={16}
              />
            </button>

            <AccordionPanel isExpanded={isErrorsExpanded}>
              <div className={styles.validationAccordionContent}>
                <Text color="secondary" variant="body">
                  {TEXT.validationWarningIntro}
                </Text>
                <div className={styles.validationListContainer}>
                  <ul className={styles.validationList}>
                    {failedAssets.map((asset) => (
                      <li key={asset.id} className={styles.validationListItem}>
                        <div className={styles.validationItemContent}>
                          <Text
                            as="span"
                            display="block"
                            fontWeight="semiBold"
                            variant="body"
                          >
                            {asset.name || asset.id}
                          </Text>
                          <Text
                            as="span"
                            color="secondary"
                            display="block"
                            variant="body"
                          >
                            {asset.errors
                              .map((error) =>
                                getErrorLabel(
                                  error,
                                  error === 'invalid_codec'
                                    ? asset.codec
                                    : undefined
                                )
                              )
                              .join(', ')}
                          </Text>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                {validAssetIds.length > 0 && (
                  <Text color="secondary" variant="body">
                    {TEXT.validationWarningContinue}
                  </Text>
                )}
              </div>
            </AccordionPanel>
          </div>
        )}

        <Toggle
          checked={includeOtio}
          className={styles.toggleWrapper}
          onChange={() => setIncludeOtio(!includeOtio)}
        >
          <ToggleSwitch />
          <ToggleLabel>
            <div className={styles.labelRow}>
              <Text color="primary" variant="body">
                {TEXT.includeOtioFile}
              </Text>
              <Tooltip placement="right" title={TEXT.includeOtioFileTooltip}>
                <IconInfo color={theme.colors.icon.primary} size={16} />
              </Tooltip>
            </div>
          </ToggleLabel>
        </Toggle>

        <Text color="secondary" fontWeight="regular" variant="caption">
          {TEXT.infoText}{' '}
          <InlineButton
            onPress={() =>
              window.open(INFO_TEXT_HREF, '_blank', 'noopener,noreferrer')
            }
          >
            <Text color="link" variant="linkSmall">
              {TEXT.learnMore}
            </Text>
          </InlineButton>
        </Text>
      </div>

      <Divider />
      <DialogFooter>
        <Button2 onPress={handleClose} variant="secondary">
          {TEXT.cancel}
        </Button2>
        <Button2
          isDisabled={
            selectedAspectRatios.length === 0 ||
            loading ||
            validAssetIds.length === 0 ||
            Boolean(focusSubjectError)
          }
          isLoading={loading}
          onPress={handleGenerate}
          variant="primary"
        >
          {TEXT.generate}
        </Button2>
      </DialogFooter>
      {rateLimitDialog}
    </Dialog>
  );
};
